import type { TaskResponse } from '@/store/tasks/tasks.dto';

import { TaskStatus } from '../CreateSheet/constants';

// Statuses whose deadline still means something — both predicates below ignore the rest.
// Exported so a request can pre-filter on the same set these check client-side.
export const OPEN_TASK_STATUSES: string[] = [TaskStatus.PENDING, TaskStatus.IN_PROGRESS];

/** Widest a due-soon window ever gets, so a query can bound the deadline it fetches. */
export const DUE_SOON_MAX_WINDOW_MS = 24 * 60 * 60 * 1000;

export const isTaskDueSoon = (task: TaskResponse) => {
  if (!OPEN_TASK_STATUSES.includes(task.status)) return false;

  const now = new Date();
  const created = new Date(task.time_slot.start);
  const deadline = new Date(task.time_slot.end);

  if (now >= deadline) return false;

  const lifespan = deadline.getTime() - created.getTime();

  if (lifespan <= 60 * 60 * 1000) return false;

  if (now.getTime() - created.getTime() < 60 * 60 * 1000) return false;

  let dueSoonWindow = lifespan * 0.25;

  const MIN = 30 * 60 * 1000;

  dueSoonWindow = Math.max(MIN, Math.min(DUE_SOON_MAX_WINDOW_MS, dueSoonWindow));

  const timeLeft = deadline.getTime() - now.getTime();

  return timeLeft <= dueSoonWindow;
};

/** Overdue: still open, deadline already passed. */
export const isTaskExpired = (task: TaskResponse) => {
  if (!OPEN_TASK_STATUSES.includes(task.status)) return false;
  return new Date() > new Date(task.time_slot.end);
};

/**
 * Upcoming: still open with its deadline far enough ahead that it hasn't entered
 * the due-soon window — the remainder of the open tasks, so overdue / due soon /
 * upcoming partition them with nothing falling between the three.
 */
export const isTaskUpcoming = (task: TaskResponse) => {
  if (!OPEN_TASK_STATUSES.includes(task.status)) return false;
  return !isTaskExpired(task) && !isTaskDueSoon(task);
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const MONTH_MS = 30 * DAY_MS;
const YEAR_MS = 365 * DAY_MS;

// Coarsest unit that still yields a whole count wins, so a deadline reads "40 min",
// then "5 h", then "3 days", then "2 months" as it recedes — never "4800 min".
// Months and years are the usual 30/365-day approximations: this is a span read at a
// glance, not an accrual, and the exact date rides along in the cell's tooltip.
const SPAN_UNITS = [
  { ms: YEAR_MS, singular: 'year', plural: 'years' },
  { ms: MONTH_MS, singular: 'month', plural: 'months' },
  { ms: DAY_MS, singular: 'day', plural: 'days' },
  { ms: HOUR_MS, singular: 'h', plural: 'h' },
  { ms: MINUTE_MS, singular: 'min', plural: 'min' },
] as const;

/** Empty under a minute, which the caller turns into "Due now" / "Overdue". */
const formatSpan = (ms: number): string => {
  for (const { ms: unitMs, singular, plural } of SPAN_UNITS) {
    const count = Math.floor(ms / unitMs);
    if (count >= 1) return `${count} ${count === 1 ? singular : plural}`;
  }
  return '';
};

/**
 * "Due in 3 days" / "Overdue by 5 h" — the wording TaskExpiringLabel and
 * TaskExpiredLabel already use in their tooltips, with the unit picked to fit.
 */
export const formatDueSpan = (deadline: string): string => {
  const diffMs = new Date(deadline).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return '';
  const span = formatSpan(Math.abs(diffMs));
  if (!span) return diffMs >= 0 ? 'Due now' : 'Overdue';
  return diffMs >= 0 ? `Due in ${span}` : `Overdue by ${span}`;
};
